import axios from "axios";

const CSRF_COOKIE = "csrftoken";
const SAFE_METHODS = ["get", "head", "options", "trace"];

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "/api";

/**
 * Every API error reaches the UI as one of these, so components never have to
 * dig through axios internals.
 *
 * `fieldErrors` mirrors the backend's `errors` object: { field: [messages] }.
 */
export class ApiError extends Error {
  constructor(message, { status = null, code = null, fieldErrors = null } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors;
  }

  /**
   * No usable session: the user has to sign in again.
   *
   * Django's session authentication answers both "no session" and "wrong role"
   * with 403, so the two are told apart by the code the API sends.
   */
  get isUnauthorized() {
    return this.status === 401 || this.code === "not_authenticated";
  }

  /** Signed in, but the role does not allow this. Signing in again won't help. */
  get isForbidden() {
    return this.status === 403 && !this.isUnauthorized;
  }
}

/**
 * Any request can be the one that discovers the session has expired, so the
 * client reports it in one place instead of every caller checking. AuthProvider
 * is the only intended subscriber; it returns an unsubscribe function.
 */
let sessionExpiredHandler = null;

export function onSessionExpired(handler) {
  sessionExpiredHandler = handler;
  return () => {
    if (sessionExpiredHandler === handler) {
      sessionExpiredHandler = null;
    }
  };
}

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

function readCookie(name) {
  const match = document.cookie.match(new RegExp(`(^|;\\s*)${name}=([^;]*)`));
  return match ? decodeURIComponent(match[2]) : null;
}

/**
 * Django only issues the CSRF cookie when something asks for it. Requests that
 * need it wait on a single shared fetch rather than firing one each.
 */
let csrfRequest = null;
let csrfToken = null;

async function ensureCsrfCookie({ force = false } = {}) {
  const cookieToken = readCookie(CSRF_COOKIE);
  if (!force && (csrfToken || cookieToken)) {
    csrfToken ||= cookieToken;
    return;
  }
  if (!csrfRequest) {
    csrfRequest = axios
      .get(`${API_BASE_URL}/auth/csrf/`, { withCredentials: true })
      .then((response) => {
        csrfToken = response.data?.csrf_token || readCookie(CSRF_COOKIE);
      })
      .finally(() => {
        csrfRequest = null;
      });
  }
  await csrfRequest;
}

apiClient.interceptors.request.use(async (config) => {
  const method = (config.method ?? "get").toLowerCase();
  if (!SAFE_METHODS.includes(method)) {
    await ensureCsrfCookie();
    config.headers["X-CSRFToken"] = csrfToken || readCookie(CSRF_COOKIE);
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config } = error;

    if (!response) {
      throw new ApiError("Unable to reach the server. Check your connection and try again.", {
        code: "network_error",
      });
    }

    const data = response.data ?? {};

    // A stale CSRF token is recoverable: fetch a fresh one and replay once.
    if (response.status === 403 && data.code === "csrf_failure" && !config._csrfRetried) {
      config._csrfRetried = true;
      await ensureCsrfCookie({ force: true });
      return apiClient.request(config);
    }

    const apiError = new ApiError(data.detail ?? "Something went wrong. Please try again.", {
      status: response.status,
      code: data.code ?? null,
      fieldErrors: data.errors ?? null,
    });

    // Notifying rather than redirecting here keeps this free of routing and of
    // retries: the subscriber only updates state, so there is no loop to fall
    // into no matter how many requests fail at once.
    if (apiError.isUnauthorized) {
      sessionExpiredHandler?.();
    }

    throw apiError;
  },
);

export { ensureCsrfCookie };
